import React from 'react';
import styled from 'styled-components';

import { fetchOrganization } from './api';
import { useAsyncResource, getSuspension, unwrapResponse as unwrap } from './hooks';
import { isClubKind, useOrgKind, vocabularyForKind } from './vocabulary';
import SuspendedNotice from './SuspendedNotice';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch (e) {
    return iso;
  }
};

const TeamAdminSettings = () => {
  const storedKind = useOrgKind();
  const org = useAsyncResource(fetchOrganization, []);
  const data = unwrap(org.data);
  const kind = data?.kind || storedKind;
  const isClub = isClubKind(kind);
  const v = vocabularyForKind(kind, data?.name);
  const suspension = getSuspension(org.error);

  const accessValue = (() => {
    if (!data) return '—';
    if (data.suspended_at) return 'Switched off';
    if (data.access_ends_at) return `Until ${formatDate(data.access_ends_at)}`;
    return 'Open-ended';
  })();

  return (
    <Wrap>
      <h1>{v.settingsTitle}</h1>

      {suspension && <SuspendedNotice data={suspension} vocabulary={v} />}
      {!suspension && org.loading && <Placeholder>Loading...</Placeholder>}
      {!suspension && org.error && <Error>Failed to load {isClub ? 'club' : 'organization'}.</Error>}

      {data && isClub && (
        <Card>
          <Row><Label>Club</Label><Value>{data.team?.name || data.name}</Value></Row>
          <Row><Label>Access</Label><Value>{accessValue}</Value></Row>
          <Row><Label>Players</Label><Value>{data.active_player_count ?? 0} with app access · no limit</Value></Row>
          <Row><Label>Contact e-mail</Label><Value>{data.contact_email || '—'}</Value></Row>
          <Row><Label>Status</Label><Value>{data.is_operational ? 'Active' : 'Switched off'}</Value></Row>
        </Card>
      )}

      {data && !isClub && (
        <Card>
          <Row><Label>Name</Label><Value>{data.name}</Value></Row>
          {data.slug && <Row><Label>Slug</Label><Value>{data.slug}</Value></Row>}
          <Row><Label>Plan</Label><Value style={{ textTransform: 'capitalize' }}>{data.subscription_tier || 'basic'}</Value></Row>
          <Row><Label>Player seats</Label><Value>{data.seats_unlimited || data.max_player_seats == null ? 'Unlimited' : data.max_player_seats}</Value></Row>
          <Row><Label>Billing email</Label><Value>{data.billing_email || '—'}</Value></Row>
          <Row><Label>Status</Label><Value>{data.is_active ? 'Active' : 'Inactive'}</Value></Row>
        </Card>
      )}

      <Note>{v.settingsNote}</Note>
    </Wrap>
  );
};

export default TeamAdminSettings;

const Wrap = styled.div`padding: 24px; max-width: 720px; margin: 0 auto;`;
const Card = styled.div`background: ${(p) => p.theme.black2 || 'rgba(255,255,255,0.04)'}; border: 1px solid ${(p) => p.theme.lines}; border-radius: 14px; padding: 20px; margin: 20px 0;`;
const Row = styled.div`display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px solid ${(p) => p.theme.lines};
  &:last-child { border-bottom: none; }`;
const Label = styled.div`color: ${(p) => p.theme.blueish2}; font-size: 13px; text-transform: uppercase; letter-spacing: 0.08em;`;
const Value = styled.div`font-family: 'SatoshiBold';`;
const Placeholder = styled.div`padding: 24px; text-align: center; color: ${(p) => p.theme.blueish2};`;
const Error = styled.div`padding: 10px 14px; background: rgba(255, 80, 80, 0.1); border: 1px solid rgba(255, 80, 80, 0.4); border-radius: 10px; color: #ff7070; font-size: 14px;`;
const Note = styled.p`color: ${(p) => p.theme.blueish2}; font-size: 13px; line-height: 1.6;`;
